import { ProjectFormCreationProps, ProjectProps } from "../interfaces/project_types"
import AxiosInstance from "../components/Axios"
import { AxiosError, AxiosResponse } from "axios"
import { API, APIResponse } from "@/components/_Shared/API/API"
import {
    GET_PROJECTS,
    GET_PROJECT_BY_ID,
    CREATE_PROJECT,
    UPDATE_PROJECT,
    DELETE_PROJECT,
    GET_ACTIVE_PROJECTS,
    GET_PROJECT_BY_DATE,
    UPDATE_PROJECT_DESCRIPTION,
    UPDATE_CONSULTING_FEE,
    UPDATE_CHECKLIST_ITEM,
} from "./Endpoints/projects"

/**
 * Fetches a list of projects
 * 
 * uses the project_list() view in the backend
 * 
 * @ filter optional: filters the list of projects based on field
 * @ returns (supposed to be) a list of ProjectProps
 */
export async function getProjectList(filter?: string): Promise<APIResponse<ProjectProps[]>> {
    const response = await API.get<ProjectProps[]>(GET_PROJECTS, { params: filter ? { filter } : {} })
    return response
}

export async function getActiveProjects(): Promise<APIResponse<ProjectProps[]>> {
    const response = await API.get<ProjectProps[]>(GET_ACTIVE_PROJECTS)
    return response
}

/**
 * Fetches a single project based on id
 * 
 * @param id the project id of the project
 * @returns An object of type UpdateProjectProps
 */
export async function getProject(id: string): Promise<APIResponse<ProjectProps>> {
    const response = await API.get<ProjectProps>(GET_PROJECT_BY_ID.replace("{id}", id))
    return response
}

/**
 * Creates a Trinity Project
 *
 * The project_data should be in the format of ProjectPOSTProps
 *
 * @param project_data
 */
export async function createProject(project_data: ProjectFormCreationProps): Promise<APIResponse> {
    const response = await API.post<void>(CREATE_PROJECT, project_data)
    return response
}

export async function updateProject(project_data: ProjectFormCreationProps): Promise<APIResponse> {
    const response = await API.put<void>(UPDATE_PROJECT.replace("{id}", project_data.project_id ?? ""), project_data)
    return response
}

export async function deleteProject(id: string): Promise<number> {
    try {
        const response = await AxiosInstance.delete(DELETE_PROJECT.replace("{id}", id))
        return response.status
    } catch (error: unknown) {
        if (error instanceof AxiosError) {
            if (error.response?.status === 400) {
                // 400 Bad Request
                return 400
            } else if (error.response?.status === 403) {
                // 403 Forbidden
                return 403
            }
        }
        console.error("Server Error: ", error)
        return 500
    }
}

export async function getProjectByDate(year: string, month: string, day_range: string): Promise<ProjectProps[]> {
    try {
        const response = await AxiosInstance.get(GET_PROJECT_BY_DATE, { params: { year: year, month: month, day_range: day_range} })

        if (!response) {
            throw new Error("Error fetching project list")
        }

        return response.data

    } catch (error) {  
        console.error("Server Error: ",error)
        throw error
    }
}

export async function updateConsultingFee(project_id: string, consulting_fee: number): Promise<number> {
    try {
        const response = await AxiosInstance.put(UPDATE_CONSULTING_FEE.replace("{id}", project_id), {consulting_fee: consulting_fee})
        return response.status
    } catch (error) {
        console.error("Server Error: ", error)
        return 500
    }
}


export async function updateProjectDescription(project_id: string, description: string): Promise<number> {
    try {
        const response = await AxiosInstance.put(UPDATE_PROJECT_DESCRIPTION.replace("{id}", project_id), {description: description})
        return response.status
    } catch (error) {
        console.error("Server Error: ", error)
        return 500
    }
}

export async function updateChecklistItem(project_id: string, field_name: string, completed: boolean, date?: string): Promise<AxiosResponse> {
    try {
        const response = await AxiosInstance.put(UPDATE_CHECKLIST_ITEM.replace("{id}", project_id), 
            {
                field_name: field_name, 
                completed: completed, 
                date: date,
            }
        )
        return response
    } catch (error) {
        console.error("Server Error: ", error)
        throw error
    }
}